import { useEffect, useMemo, useState } from "react";
import { api, downloadBlobResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Users, Activity, Building2, GraduationCap, MapPin, FileSpreadsheet, FileText, Archive, RotateCcw } from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
    PieChart, Pie, Cell, LabelList,
} from "recharts";
import { chartTooltipProps, formatAngkaTooltip } from "@/lib/chartTooltip";
import { toast } from "sonner";
import { seesAllProdi, isAdminKoas, istilahProdi } from "@/lib/roles";
import { InfoDefinisiAktif, StatusLegend, CekAngka, PerluDilengkapi, SebaranSemester, persen, angka } from "@/components/DashboardPanels";
import {
    SEMUA_JENJANG, angkaKartu, teksLingkup, lingkupKartuJenjang, rincianStasePerJenjang, paramUnduhDashboard,
} from "@/lib/kartuDashboard";

// Warna titik/irisan status: semua >= 3:1 terhadap kartu terang (#FFF) DAN gelap
// (--card 12%) -- Lulus blue-600, Tidak Aktif slate-500, Meninggal purple-500 (dulu
// dua abu gelap yg nyaris sama & hilang di tema gelap).
const STATUS_COLORS = { "Aktif": "#15803D", "Cuti": "#B45309", "Lulus": "#2563EB", "Tidak Aktif": "#64748B", "Meninggal": "#A855F7" };
// Urutan tetap daftar & irisan Status Keaktifan (bukan urutan data); status lain (mis.
// "Tidak Diisi" dari data lama) menyusul di akhir. Status berjumlah 0 disembunyikan.
const STATUS_URUT = ["Aktif", "Lulus", "Cuti", "Tidak Aktif", "Meninggal"];
function urutkanStatus(rows) {
    const posisi = (label) => (STATUS_URUT.includes(label) ? STATUS_URUT.indexOf(label) : STATUS_URUT.length);
    return rows.filter((r) => r.count > 0).sort((a, b) => posisi(a.label) - posisi(b.label));
}
const TAHAP_COLORS = { "Tahap 1": "#0F766E", "Tahap 2": "#4338CA", "Tahap 3": "#86198F" };
// Bulatan daftar "Tahap Sp1". MDU/MDK & "Belum diisi" sama-sama token --muted-foreground
// (4,70:1 terang / 6,52:1 gelap), dibedakan bentuknya: MDU/MDK penuh, "Belum diisi"
// cincin kosong -- di tema gelap token ini identik dgn abu lama (#94A3B8), jadi warna
// saja tidak cukup membedakan.
const TAHAP_ABU = "hsl(var(--muted-foreground))";
function tahapDotStyle(label) {
    if (TAHAP_COLORS[label]) return { background: TAHAP_COLORS[label] };
    if (label === "MDU/MDK") return { background: TAHAP_ABU };
    if (label === "Belum diisi") return { border: `2px solid ${TAHAP_ABU}` };
    return { background: "#94A3B8" };
}
const MONTHS = [
    "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

// Angka "RSCM / Luar" (mis. "1738 / 681") jauh lebih lebar dari angka tunggal & tidak muat
// di kartu 5-kolom dgn text-3xl (kartu maks ~230px) -- dikecilkan bertahap sesuai ruang
// yg tersedia di tiap lebar layar supaya tetap SATU baris & tidak terpotong.
const KPI_VALUE_STASE = "text-3xl lg:text-sm xl:text-lg min-[1366px]:text-xl min-[1440px]:text-2xl 2xl:text-[28px]";

// Grafik "Jumlah PPDS per Program Studi" (hanya peserta Aktif, lihat compute_summary):
// semua label prodi tampil (interval 0) dgn nomor urutan kecil di kiri, nama dibungkus
// maks 2 baris (tidak dipotong). Tinggi grafik mengikuti jumlah batang.
const PRODI_AXIS_WIDTH = { lebar: 200, sempit: 140 }; // sempit = < 640px; 140 = nama terpanjang 2 baris muat
const PRODI_NUMBER_COL = 18; // ruang nomor urutan di kiri nama
const PRODI_TICK_GAP = 8; // = tickMargin YAxis (tickSize 0) -> x tick = tepi kanan sumbu - 8px
const PRODI_ROW_HEIGHT = 34;
const PRODI_BAR_SIZE = 20;
const PRODI_TOP_HIGHLIGHT = 3;
// Palet index.css: --brand-teal (lebih gelap; 4,94:1 di kartu terang, 3,37:1 di kartu
// gelap -- lolos 3:1 utk elemen grafis) utk 3 teratas, --brand-turq-dark utk sisanya.
const PRODI_BAR_TOP = "#267B84";
const PRODI_BAR = "#00B9AD";
const MEDIA_SEMPIT = "(max-width: 639px)";

let measureCtx = null;
function textWidth(text, font) {
    if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
    measureCtx.font = font;
    return measureCtx.measureText(text).width;
}

// Bungkus nama maks 2 baris, dipecah di batas kata yg membuat baris TERLEBAR sekecil
// mungkin (diukur dlm piksel). Nama yg muat 1 baris tidak dipecah. Tidak dipotong.
function wrapProdiName(name, maxWidth, font) {
    const words = String(name || "").split(/\s+/).filter(Boolean);
    const full = words.join(" ");
    if (words.length < 2 || textWidth(full, font) <= maxWidth) return [full];
    let best = null;
    for (let i = 1; i < words.length; i++) {
        const lines = [words.slice(0, i).join(" "), words.slice(i).join(" ")];
        const widest = Math.max(...lines.map((l) => textWidth(l, font)));
        if (!best || widest < best.widest) best = { lines, widest };
    }
    return best.lines;
}

function ProdiAxisTick({ x, y, payload, index, fill, axisWidth, fontSize }) {
    const font = `${fontSize}px ${getComputedStyle(document.body).fontFamily}`;
    const axisLeft = x + PRODI_TICK_GAP - axisWidth;
    const lines = wrapProdiName(payload?.value, axisWidth - PRODI_TICK_GAP - PRODI_NUMBER_COL, font);
    const lineHeight = fontSize + 2;
    const firstDy = lines.length === 1 ? fontSize * 0.35 : fontSize * 0.35 - lineHeight / 2;
    return (
        <g>
            <text x={axisLeft + 2} y={y} dy={fontSize * 0.35} textAnchor="start" fontSize={fontSize - 1} fontWeight={600} fill={fill}>
                {index + 1}
            </text>
            <text x={x} y={y} textAnchor="end" fontSize={fontSize} fill={fill}>
                {lines.map((line, i) => (
                    <tspan key={i} x={x} dy={i === 0 ? firstDy : lineHeight}>{line}</tspan>
                ))}
            </text>
        </g>
    );
}

// "Nama Prodi (Jenjang)" -- kecuali nama sudah diakhiri jenjangnya sendiri.
function labelProdi({ nama_prodi: nama, jenjang }) {
    const akhiran = `(${jenjang})`;
    return String(nama || "").trim().endsWith(akhiran) ? nama : `${nama} ${akhiran}`;
}

function ProdiTooltip({ active, payload, totalAktif, style }) {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
        <div style={{ ...style, padding: "8px 10px", fontSize: 12, lineHeight: 1.5, maxWidth: 260 }}>
            <div style={{ fontWeight: 700 }}>{row.label}</div>
            <div>{row.count.toLocaleString("id-ID")} peserta aktif</div>
            <div>{persen(row.count, totalAktif)} dari total peserta aktif</div>
        </div>
    );
}

function Kpi({ icon: Icon, label, info, value, sub, lingkup, redup = false, tone = "navy", testId, valueClassName = "text-3xl", className = "" }) {
    // Teks gelap wajib di atas turquoise-dark/lime (putih di atas warna itu gagal AA, <4.5:1).
    const tones = {
        navy: "bg-[#267B84] text-white",
        teal: "bg-[#00B9AD] text-[#0F2E30]",
        lime: "bg-[#CDDC29] text-[#0F2E30]",
        turqMedium: "bg-[#60C0D0] text-[#0F2E30]",
    };
    return (
        // h-full berantai (Card -> CardContent -> baris flex) supaya blok warna kiri
        // meregang setinggi KARTU (yg ikut tinggi baris grid), bukan setinggi isinya.
        // Lebar blok dikunci (shrink-0) kecuali di lg (1024-1279px) -- di situ kartu 5-kolom
        // terlalu sempit, blok boleh menyempit spt sebelumnya supaya angka tidak terpotong.
        <Card data-testid={testId} data-redup={redup || undefined} className={`h-full border-slate-200 shadow-sm overflow-hidden ${className}`}>
            <CardContent className="h-full p-0">
                <div className="flex h-full">
                    {/* redup (kartu jenjang di luar filter): hanya blok warna dekoratif yg diredupkan -- teks tetap kontras penuh. */}
                    <div className={`${tones[tone]} ${redup ? "opacity-40" : ""} w-14 shrink-0 lg:shrink xl:shrink-0 flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 p-4">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">{label}{info}</div>
                        <div className={`${valueClassName} font-extrabold kpi-value text-slate-900 mt-1 whitespace-nowrap`}>{value}</div>
                        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
                        {/* Lingkup angka kartu (jenjang & status) -- ikut filter Jenjang, lihat lib/kartuDashboard.js. */}
                        {lingkup && <div className="text-[11px] text-slate-500 mt-1 leading-snug" data-testid={testId && `${testId}-lingkup`}>{lingkup}</div>}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    // Role terkunci 1 jenjang (admin_prodi/admin_koas): filter "Jenjang" pada grafik
    // per-prodi tidak berguna bagi mereka (summary.per_prodi memang sudah cuma berisi
    // jenjang mereka sendiri, hasil "Semua Jenjang" vs jenjang spesifik identik) --
    // sembunyikan daripada menawarkan pilihan yang tidak ada gunanya. isJenjangLocked
    // sendiri langsung diketahui dari role (tanpa jeda); jenjang SPESIFIK-nya (lihat
    // lockedJenjang di bawah, dipakai utk showTahapSp1) baru diketahui setelah
    // prodiList termuat utk admin_prodi.
    const isJenjangLocked = !seesAllProdi(user.role);
    const isKoas = isAdminKoas(user.role);
    const istilah = istilahProdi(user.role);
    const { effectiveTheme } = useTheme();
    const isDark = effectiveTheme === "dark";
    // Recharts render SVG mentah (bukan kelas Tailwind) -> tidak kena override
    // CSS global di index.css, jadi warna grid/axis di-set manual di sini (tooltip: lib/chartTooltip.js).
    const chartGridStroke = isDark ? "#3A3A3A" : "#E2E8F0";
    const chartTickFill = isDark ? "#94A3B8" : "#475569";
    // Angka di ujung batang: teks gelap (--brand-text-dark) di tema terang; di tema gelap
    // teks terang -- teks gelap di atas latar kartu gelap tidak akan terbaca.
    const chartValueFill = isDark ? "#E2E8F0" : "#0F2E30";
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [archiveMissing, setArchiveMissing] = useState(false);
    const [jenjangFilter, setJenjangFilter] = useState("all");
    const [downloading, setDownloading] = useState(false);

    const [prodiList, setProdiList] = useState([]);
    const [snapshots, setSnapshots] = useState([]);
    // period: { mode: "live" } | { mode: "snapshot", bulan, tahun }
    const [period, setPeriod] = useState({ mode: "live" });
    const [selYear, setSelYear] = useState("");
    const [selMonth, setSelMonth] = useState("");

    const years = useMemo(
        () => [...new Set(snapshots.map((s) => s.tahun))].sort((a, b) => b - a),
        [snapshots],
    );
    const monthsForYear = useMemo(() => {
        if (!selYear) return [];
        return snapshots
            .filter((s) => String(s.tahun) === String(selYear))
            .map((s) => s.bulan)
            .sort((a, b) => a - b);
    }, [snapshots, selYear]);

    const loadSnapshots = async () => {
        try { const { data } = await api.get("/snapshot/list"); setSnapshots(data); } catch {}
    };

    const loadSummary = async (p) => {
        setLoading(true);
        setArchiveMissing(false);
        try {
            if (p.mode === "snapshot") {
                const { data } = await api.get(`/snapshot/${p.bulan}/${p.tahun}`);
                setSummary(data);
            } else {
                const { data } = await api.get("/dashboard/summary");
                setSummary(data);
            }
        } catch (e) {
            if (p.mode === "snapshot" && e.response?.status === 404) {
                setSummary(null);
                setArchiveMissing(true);
            } else {
                toast.error("Gagal memuat dashboard");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSnapshots(); }, []);
    useEffect(() => { loadSummary(period); /* eslint-disable-line */ }, [period]);
    // Hanya admin_prodi yg perlu tahu jenjang prodinya sendiri (utk sembunyikan seksi
    // "Tahap Sp1" kalau bukan Sp1, lihat showTahapSp1) -- admin_koas sudah pasti "Koas"
    // tanpa perlu data ini, role bebas (isSuper) tidak terkunci 1 jenjang sama sekali.
    useEffect(() => {
        if (isJenjangLocked && !isKoas) {
            api.get("/prodi").then(({ data }) => setProdiList(data)).catch(() => {});
        }
    }, [isJenjangLocked, isKoas]);

    // Saat tahun & bulan dipilih -> pindah ke mode snapshot
    useEffect(() => {
        if (selYear && selMonth) {
            setPeriod({ mode: "snapshot", bulan: Number(selMonth), tahun: Number(selYear) });
        }
    }, [selYear, selMonth]);

    const backToLive = () => {
        setSelYear(""); setSelMonth("");
        setPeriod({ mode: "live" });
    };

    const { perProdi, perProdiTotalAktif } = useMemo(() => {
        if (!summary) return { perProdi: [], perProdiTotalAktif: 0 };
        let rows = summary.per_prodi;
        if (jenjangFilter !== "all") rows = rows.filter((r) => r.jenjang === jenjangFilter);
        return {
            // Nama prodi Sp1 & Sp2 bisa sama persis (mis. "Ilmu Penyakit Dalam") -- jenjang
            // ditambahkan di label supaya tidak terlihat seperti prodi ganda.
            perProdi: [...rows].sort((a, b) => b.count - a.count).slice(0, 20)
                .map((r) => ({ ...r, label: labelProdi(r) })),
            // Pembagi persentase tooltip: SEMUA baris per_prodi pd filter jenjang ini,
            // bukan cuma 20 teratas yg digambar.
            perProdiTotalAktif: rows.reduce((sum, r) => sum + r.count, 0),
        };
    }, [summary, jenjangFilter]);
    const [isSempit, setIsSempit] = useState(() => window.matchMedia(MEDIA_SEMPIT).matches);
    useEffect(() => {
        const mq = window.matchMedia(MEDIA_SEMPIT);
        const onChange = (e) => setIsSempit(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    const prodiAxisWidth = isSempit ? PRODI_AXIS_WIDTH.sempit : PRODI_AXIS_WIDTH.lebar;
    const prodiTickFontSize = isSempit ? 10 : 11;

    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p));
        return m;
    }, [prodiList]);
    // Sama spt pola lockedJenjang di pages/Peserta.jsx -- admin_koas selalu "Koas",
    // admin_prodi baru "diketahui" setelah prodiList termuat (sebelum itu
    // showTahapSp1 sengaja false dulu, bukan sempat tampil Sp1 yg salah).
    const lockedJenjang = isKoas ? "Koas" : (isJenjangLocked ? prodiMap[user.prodi_id]?.jenjang : null);
    const showTahapSp1 = !isJenjangLocked || lockedJenjang === "Sp1";

    // Kartu ikut filter Jenjang (akun terkunci 1 jenjang: tanpa filter, data server sudah sebatas cakupannya).
    const jenjangKartu = isJenjangLocked ? SEMUA_JENJANG : jenjangFilter;
    const kartu = useMemo(() => (summary ? angkaKartu(summary, jenjangKartu) : null), [summary, jenjangKartu]);
    const lingkup = kartu && teksLingkup({
        jenjang: jenjangKartu, tersaring: kartu.tersaring, terkunci: isJenjangLocked, jenjangTerkunci: lockedJenjang,
    });
    const rincianStase = summary && !isJenjangLocked && jenjangKartu === SEMUA_JENJANG ? rincianStasePerJenjang(summary) : null;

    const download = async (fmt) => {
        setDownloading(true);
        try {
            // Unduhan Dashboard ikut filter Jenjang (Semua = semua jenjang, spt sebelumnya); isi tetap semua status.
            const res = await api.get(`/export/${fmt}`, {
                params: paramUnduhDashboard(jenjangFilter, isJenjangLocked), responseType: "blob",
            });
            downloadBlobResponse(res, `aura_dashboard.${fmt === "excel" ? "xlsx" : "pdf"}`);
        } catch {
            toast.error("Gagal export");
        } finally {
            setDownloading(false);
        }
    };

    const isArchive = period.mode === "snapshot";
    // Admin Prodi: grafik per prodi cuma berisi satu batang -> diganti "Perlu dilengkapi"
    // + sebaran Semester prodinya.
    const isAdminProdi = user.role === "admin_prodi";
    const statusRows = useMemo(() => (summary ? urutkanStatus(summary.status_distribution) : []), [summary]);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Dashboard</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        {seesAllProdi(user.role) ? "Ikhtisar Unit Fungsional Pendidikan" : isKoas ? "Ikhtisar Peserta Koas" : "Ikhtisar Program Studi"}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {isArchive
                            ? "Menampilkan data arsip (snapshot) — bukan data terkini."
                            : "Real-time monitoring peserta didik spesialis di RSCM."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button data-testid="export-excel-button" variant="outline" onClick={() => download("excel")} disabled={downloading}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                    </Button>
                    <Button data-testid="export-pdf-button" variant="outline" onClick={() => download("pdf")} disabled={downloading}>
                        <FileText className="w-4 h-4 mr-2" /> PDF
                    </Button>
                </div>
            </div>

            {/* Pemilih periode data */}
            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Archive className="w-4 h-4 text-slate-500" /> Periode Data
                    </div>
                    <Select value={selYear} onValueChange={(v) => { setSelYear(v); setSelMonth(""); }}>
                        <SelectTrigger className="w-32 h-9" data-testid="dashboard-year-select">
                            <SelectValue placeholder="Tahun" />
                        </SelectTrigger>
                        <SelectContent>
                            {years.length === 0
                                ? <SelectItem value="__none" disabled>Belum ada arsip</SelectItem>
                                : years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={selMonth} onValueChange={setSelMonth} disabled={!selYear}>
                        <SelectTrigger className="w-40 h-9" data-testid="dashboard-month-select">
                            <SelectValue placeholder="Bulan" />
                        </SelectTrigger>
                        <SelectContent>
                            {monthsForYear.map((m) => <SelectItem key={m} value={String(m)}>{MONTHS[m]}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {isArchive ? (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                Arsip: {summary?.label || `${MONTHS[period.bulan]} ${period.tahun}`}
                            </span>
                            <Button size="sm" variant="outline" onClick={backToLive} data-testid="dashboard-back-live">
                                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Data Terkini
                            </Button>
                        </div>
                    ) : (
                        <span className="text-xs text-slate-400">Default: data terkini (live). Pilih tahun &amp; bulan untuk melihat arsip.</span>
                    )}
                    {/* Filter Jenjang: kartu angka, grafik per prodi, dan unduhan Excel/PDF Dashboard. */}
                    {!isJenjangLocked && (
                        <div className="flex items-center gap-2 sm:ml-auto">
                            <span className="text-sm font-semibold text-slate-700">Jenjang</span>
                            <Select value={jenjangFilter} onValueChange={setJenjangFilter}>
                                <SelectTrigger className="w-36 h-9" data-testid="dashboard-jenjang-filter" aria-label="Filter jenjang">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Jenjang</SelectItem>
                                    <SelectItem value="Sp1">Sp1</SelectItem>
                                    <SelectItem value="Sp2">Sp2</SelectItem>
                                    <SelectItem value="Koas">Koas</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </CardContent>
            </Card>

            {loading ? (
                <div className="p-10 text-slate-500">Memuat data...</div>
            ) : archiveMissing ? (
                <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-12 text-center">
                        <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-lg font-bold text-slate-700">Belum ada data arsip untuk periode ini</div>
                        <p className="text-sm text-slate-500 mt-1">
                            Snapshot untuk {MONTHS[period.bulan]} {period.tahun} belum dibuat.
                        </p>
                        <Button variant="outline" className="mt-4" onClick={backToLive}>
                            <RotateCcw className="w-4 h-4 mr-2" /> Kembali ke Data Terkini
                        </Button>
                    </CardContent>
                </Card>
            ) : !summary ? (
                <div className="p-10 text-slate-500">Tidak ada data.</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <Kpi icon={Users} label="Peserta Aktif" info={<InfoDefinisiAktif />} value={angka(kartu.aktif)}
                            sub={`dari ${angka(kartu.terdaftar)} terdaftar (semua status)`} lingkup={lingkup} tone="navy" testId="kpi-card-total-ppds" />
                        {[
                            { j: "Sp1", icon: Activity, sub: "Spesialis 1", tone: "teal", testId: "kpi-card-sp1", nilai: kartu.sp1_aktif },
                            { j: "Sp2", icon: Building2, sub: "Subspesialis", tone: "lime", testId: "kpi-card-sp2", nilai: kartu.sp2_aktif },
                            { j: "Koas", icon: GraduationCap, sub: "Mahasiswa kedokteran", tone: "navy", testId: "kpi-card-koas", nilai: kartu.koas_aktif },
                        ].map((k) => (
                            <Kpi key={k.j} icon={k.icon} label={`${k.j} Aktif`} value={angka(k.nilai)} sub={k.sub} tone={k.tone} testId={k.testId}
                                lingkup={lingkupKartuJenjang(k.j, jenjangKartu, kartu.tersaring)}
                                redup={kartu.tersaring && k.j !== jenjangKartu} />
                        ))}
                        <Kpi icon={MapPin} label="Stase RSCM / Luar" value={`${angka(kartu.stase.rscm)} / ${angka(kartu.stase.luar)}`}
                            sub={(
                                <span className="flex flex-wrap gap-x-2">
                                    <span>MDU-MDK: {angka(kartu.stase.mdu_mdk)}</span>
                                    <span>Penelitian: {angka(kartu.stase.penelitian)}</span>
                                    {/* ?? 0: arsip bulanan lama dibuat sebelum kategori Belum Stase ada */}
                                    <span>Belum Stase: {angka(kartu.stase.belum_stase ?? 0)}</span>
                                    <span>Belum diisi: {angka(kartu.stase.belum_diisi)}</span>
                                </span>
                            )}
                            lingkup={rincianStase ? (
                                <>
                                    <span className="block">{lingkup}</span>
                                    <span className="flex flex-wrap gap-x-2 tabular-nums" data-testid="kpi-card-stase-rincian">
                                        {rincianStase.map((r) => <span key={r}>{r}</span>)}
                                    </span>
                                </>
                            ) : lingkup}
                            tone="turqMedium" valueClassName={KPI_VALUE_STASE} testId="kpi-card-stase"
                            // Grid 2 kolom (sm-md): kartu ke-5 sendirian di baris terakhir -> pakai lebar
                            // penuh supaya "1.288 / 690" (dgn pemisah ribuan) tidak terpotong.
                            className="sm:col-span-2 lg:col-span-1" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {isAdminProdi ? (
                            <div className="lg:col-span-2 space-y-6">
                                {!isArchive && summary.perlu_dilengkapi && (
                                    <PerluDilengkapi data={summary.perlu_dilengkapi} showSp1Tahap={showTahapSp1} />
                                )}
                                <SebaranSemester rows={summary.semester_distribution} aktif={summary.aktif} barColor={PRODI_BAR_TOP} />
                            </div>
                        ) : (
                        <Card className="lg:col-span-2 border-slate-200 shadow-sm">
                            <CardContent className="p-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                                    <div>
                                        <h3 className="font-bold text-slate-800">Jumlah PPDS per {istilah.panjang}</h3>
                                        <p className="text-xs text-slate-500">
                                            Top 20 {istilah.kecil} berdasarkan jumlah peserta aktif{jenjangFilter !== SEMUA_JENJANG ? `, jenjang ${jenjangFilter}` : ""}
                                        </p>
                                    </div>
                                </div>
                                <div
                                    role="figure"
                                    aria-label={`Grafik batang jumlah peserta aktif per ${istilah.panjang.toLowerCase()}: ${perProdi.length} ${istilah.kecil} teratas${jenjangFilter !== "all" ? `, jenjang ${jenjangFilter}` : ""}.`}
                                    style={{ height: perProdi.length === 0 ? 120 : perProdi.length * PRODI_ROW_HEIGHT + 8 }}
                                >
                                    {perProdi.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-sm text-slate-400">Belum ada data peserta didik.</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={perProdi} layout="vertical" barSize={PRODI_BAR_SIZE} margin={{ top: 4, bottom: 4, left: 0, right: 48 }}>
                                                <XAxis type="number" hide domain={[0, "dataMax"]} />
                                                <YAxis
                                                    type="category" dataKey="label" width={prodiAxisWidth} interval={0}
                                                    tickLine={false} tickSize={0} tickMargin={PRODI_TICK_GAP}
                                                    axisLine={{ stroke: chartTickFill, strokeOpacity: 0.5 }}
                                                    tick={<ProdiAxisTick fill={chartTickFill} axisWidth={prodiAxisWidth} fontSize={prodiTickFontSize} />}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: chartGridStroke, fillOpacity: 0.4 }}
                                                    content={<ProdiTooltip totalAktif={perProdiTotalAktif} style={chartTooltipProps.contentStyle} />}
                                                />
                                                {/* Tanpa animasi: LabelList Recharts baru digambar setelah animasi selesai & bisa
                                                    tidak muncul sama sekali bila animasi terpotong render ulang. */}
                                                <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                                                    {perProdi.map((r, i) => (
                                                        <Cell key={`${r.prodi_id}-${r.jenjang}`} fill={i < PRODI_TOP_HIGHLIGHT ? PRODI_BAR_TOP : PRODI_BAR} />
                                                    ))}
                                                    <LabelList dataKey="count" position="right" offset={6} fontSize={13} fontWeight={700} fill={chartValueFill} formatter={angka} />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                        )}

                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-6">
                                <h3 className="font-bold text-slate-800">Status Keaktifan</h3>
                                <p className="text-xs text-slate-500 mb-2" data-testid="status-keaktifan-lingkup">
                                    Semua peserta terdaftar (semua status{!isJenjangLocked && jenjangFilter !== SEMUA_JENJANG ? ", semua jenjang — tidak mengikuti filter Jenjang" : ""})
                                </p>
                                {statusRows.length === 0 ? (
                                    <div className="h-40 flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
                                ) : (
                                    <>
                                        {/* Tanpa label irisan (bertumpuk bila irisan kecil) -- angka & persen
                                            ada di daftar di bawahnya. */}
                                        <div className="h-48" role="img" aria-label="Diagram lingkaran status keaktifan; rincian angka di daftar berikut.">
                                            <ResponsiveContainer>
                                                <PieChart>
                                                    <Pie data={statusRows} dataKey="count" nameKey="label" outerRadius={80} label={false} isAnimationActive={false} stroke="hsl(var(--card))">
                                                        {statusRows.map((s, i) => (
                                                            <Cell key={i} fill={STATUS_COLORS[s.label] || "#94A3B8"} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip
                                                        {...chartTooltipProps}
                                                        formatter={(v, name) => [`${formatAngkaTooltip(v)} (${persen(v, summary.terdaftar)})`, name]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <StatusLegend rows={statusRows} colors={STATUS_COLORS} terdaftar={summary.terdaftar} />
                                    </>
                                )}
                                <div className="mt-4">
                                    {/* Identitas dicek pada angka kartu yg sedang tampil (ikut filter Jenjang). */}
                                    <CekAngka summary={{ ...summary, ...kartu }} />
                                </div>
                                {showTahapSp1 && (
                                    <div className="mt-4 border-t pt-4">
                                        <h4 className="font-bold text-slate-800 text-sm">Tahap Sp1</h4>
                                        <p className="text-xs text-slate-500 mb-3">Peserta aktif</p>
                                        <div className="space-y-2">
                                            {summary.tahap_distribution.map((t) => (
                                                <div key={t.label} className="flex items-center gap-2">
                                                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={tahapDotStyle(t.label)} />
                                                    <div className="text-sm text-slate-700 flex-1">{t.label}</div>
                                                    <div className="text-sm font-bold text-slate-900 tabular-nums">{t.count.toLocaleString("id-ID")}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {!isAdminProdi && !isArchive && summary.perlu_dilengkapi && (
                        <PerluDilengkapi data={summary.perlu_dilengkapi} showSp1Tahap={showTahapSp1} />
                    )}
                </>
            )}
        </div>
    );
}
